import "./index.css";
import Button from "../button";
import { useState, useEffect } from "react";
import { sendLinkedinInvite } from "../../functions/sendLinkedinInvite";

function NewVoiceModule({ display, functionForButton, addVoice, updateVoice, voiceToEdit, onDisconnectLinkedin }) {
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [voiceName, setVoiceName] = useState("");
  const [voicePersona, setVoicePersona] = useState("");
  const [voiceICP, setVoiceICP] = useState("");
  const [showLinkedinInput, setShowLinkedinInput] = useState(false);
  const [linkedinEmail, setLinkedinEmail] = useState("");
  const [linkedinSending, setLinkedinSending] = useState(false);

  useEffect(() => {
    if (voiceToEdit) {
      setVoiceName(voiceToEdit.name);
      setVoicePersona(voiceToEdit.persona);
      setVoiceICP(voiceToEdit.icp);
      setLinkedinEmail(voiceToEdit.linkedinEmail || "");
    } else {
      setVoiceName("");
      setVoicePersona("");
      setVoiceICP("");
      setLinkedinEmail("");
    }
    setShowLinkedinInput(false);
  }, [voiceToEdit]);

  const getRandomColor = () => {
    const colors = ['#24e9b7', '#f67011', '#683de5', '#11e5b3', '#ff2e4d', '#fee71f', '#1e2978', '#2ba84a', '#d1f701'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const handleClose = () => {
    functionForButton();
    setVoiceName("");
    setVoiceICP("");
    setVoicePersona("");
    setLinkedinEmail("");
    setShowLinkedinInput(false);
    setIsError(false);
    setErrorMessage("");
  };

  const handleSave = async () => {
    if (!voiceName || !voicePersona || !voiceICP) {
      setIsError(true);
      setErrorMessage("All fields are required.");
      return;
    }

    const newVoice = {
      id: voiceToEdit?.id || window.crypto.randomUUID(),
      name: voiceName,
      color: voiceToEdit?.color || getRandomColor(),
      persona: voicePersona,
      icp: voiceICP,
      linkedinEmail: voiceToEdit?.linkedinEmail || null,
      linkedinStatus: voiceToEdit?.linkedinStatus || null,
    };

    if (voiceToEdit) {
      await updateVoice(newVoice);
    } else {
      await addVoice(newVoice);
    }

    setVoiceName("");
    setVoiceICP("");
    setVoicePersona("");
    setLinkedinEmail("");
    setShowLinkedinInput(false);
    setIsError(false);
    setErrorMessage("");
  };

  const handleLinkedinInvite = async () => {
    if (!linkedinEmail || !linkedinEmail.includes("@")) {
      setIsError(true);
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    if (!voiceToEdit?.id) {
      setIsError(true);
      setErrorMessage("Please save the voice first, then edit it to connect LinkedIn.");
      return;
    }
    try {
      setLinkedinSending(true);
      await sendLinkedinInvite(voiceToEdit.id, linkedinEmail);
      await updateVoice({ ...voiceToEdit, name: voiceName, persona: voicePersona, icp: voiceICP, linkedinEmail, linkedinStatus: "pending" });
      setShowLinkedinInput(false);
      setIsError(false);
      setErrorMessage("");
    } catch {
      setIsError(true);
      setErrorMessage("Failed to send LinkedIn invite. Please try again.");
    } finally {
      setLinkedinSending(false);
    }
  };

  const isConnected = voiceToEdit?.linkedinStatus === "connected";
  const isPending = voiceToEdit?.linkedinStatus === "pending";

  return (
    <div id="blur-wrapper" style={{ display }}>
      <div id="new-voice-module">

        <div id="nvm-header">
          <div>
            <p className="nvm-eyebrow">Voices</p>
            <h2 className="nvm-title highlight-green">
              {voiceToEdit ? "Edit voice" : "Create a voice"}
            </h2>
          </div>
          <button className="nvm-close-btn" onClick={handleClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M1 1l14 14M15 1L1 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div id="nvm-body">

          <div className="nvm-field">
            <label className="nvm-label">Name</label>
            <input
              className="nvm-text-input"
              type="text"
              placeholder="Who is this for?"
              value={voiceName}
              onChange={(e) => setVoiceName(e.target.value)}
            />
          </div>

          <div className="nvm-field">
            <label className="nvm-label">Persona</label>
            <textarea
              className="nvm-textarea"
              placeholder="Tell us a bit more about yourself. What's your writing style? Are you fun and casual? Informative or serious? Confident or humble? This is your persona, and it helps Mako better understand your voice."
              value={voicePersona}
              onChange={(e) => setVoicePersona(e.target.value)}
              rows={4}
            />
          </div>

          <div className="nvm-field">
            <label className="nvm-label">ICP</label>
            <textarea
              className="nvm-textarea"
              placeholder="Who are you writing for? Define your ideal customer profile — the more specific, the better. This helps Mako tailor your content to maximise engagement."
              value={voiceICP}
              onChange={(e) => setVoiceICP(e.target.value)}
              rows={4}
            />
          </div>

          {voiceToEdit && (
            <div className="nvm-field">
              <label className="nvm-label">LinkedIn</label>
              {(isConnected || isPending) ? (
                <div className={`linkedin-status-card ${voiceToEdit.linkedinStatus}`}>
                  <div className="linkedin-status-card-top">
                    <div className={`linkedin-status-badge ${voiceToEdit.linkedinStatus}`}>
                      <div className={`linkedin-status-dot ${voiceToEdit.linkedinStatus}`} />
                      {isConnected ? "Account linked" : "Invite pending"}
                    </div>
                    <span
                      className="linkedin-disconnect-link"
                      onClick={() => { onDisconnectLinkedin(voiceToEdit.id); handleClose(); }}
                    >
                      {isConnected ? "Disconnect" : "Cancel"}
                    </span>
                  </div>
                  <div className="linkedin-status-card-email">{voiceToEdit.linkedinEmail}</div>
                </div>
              ) : !showLinkedinInput ? (
                <button className="linkedin-connect-btn" onClick={() => setShowLinkedinInput(true)}>
                  Connect this voice to a LinkedIn account
                </button>
              ) : (
                <div className="linkedin-email-input-holder">
                  <input
                    type="email"
                    className="linkedin-email-input"
                    placeholder="Enter their email address"
                    value={linkedinEmail}
                    onChange={(e) => setLinkedinEmail(e.target.value)}
                  />
                  <Button text={linkedinSending ? "Sending..." : "Send invite"} smallBool={true} addedFunction={handleLinkedinInvite} />
                  <button className="nvm-ghost-btn" onClick={() => setShowLinkedinInput(false)}>Cancel</button>
                </div>
              )}
            </div>
          )}

          {isError && (
            <div id="input-error-module" dangerouslySetInnerHTML={{ __html: errorMessage }} />
          )}

          <Button text={voiceToEdit ? "Update voice" : "Create voice"} addedFunction={handleSave} />

        </div>
      </div>
    </div>
  );
}

export default NewVoiceModule;
